import React, { useEffect, useState, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Dimensions,
  Platform,
  Alert,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigationContext } from '../../navigation/NavigationContext';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useAuthStore } from '../../stores/authStore';
import { PERMISSIONS } from '../../constants/permissions';
import { VehicleGridCard } from '../../components/vehicle/VehicleGridCard';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useResponsiveStyles } from '../../hooks/useResponsiveStyles';
import { ComprehensiveVehicleTrackingGuard, FeatureGuard } from '../../features/FeatureGuard';
import { useFeatureEnabled } from '../../features/FeatureFlagProvider';
import { CORE_FEATURES } from '../../features/types';
import theme from '../../theme';
import { Vehicle } from '../../types';
import { ManualSyncButton } from '../../components/sync/ManualSyncButton';

export const VehicleListScreen = ({ navigation }: any) => {
  const { setCurrentRoute } = useNavigationContext();
  const { vehicles, loading, error, fetchVehicles, removeVehicle } = useVehicleStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'plate' | 'utilization' | 'maintenance' | 'location'>('plate');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [refreshing, setRefreshing] = useState(false);
  const responsive = useResponsiveStyles();

  // Feature flags
  const isComprehensiveVehicleTracking = useFeatureEnabled(CORE_FEATURES.COMPREHENSIVE_VEHICLE_TRACKING);
  const isMobileOptimized = useFeatureEnabled(CORE_FEATURES.MOBILE_OPTIMIZATION);

  // Responsive columns: Mobile: 2, Web: 5
  const numColumns = responsive.isWeb ? 5 : 2;

  useEffect(() => {
    fetchVehicles();
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchVehicles();
    setRefreshing(false);
  }, [fetchVehicles]);

  // Enhanced vehicle tracking actions
  const handleVehiclePress = (vehicle: Vehicle) => {
    navigation.navigate('VehicleDetails', { vehicleId: vehicle.id });
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    navigation.navigate('VehicleDetails', { vehicleId: vehicle.id, mode: 'edit' });
  };

  const handleDeleteVehicle = (vehicle: Vehicle) => {
    // Check user permission for DELETE_VEHICLES
    const hasDeletePermission = useAuthStore.getState().hasPermission(PERMISSIONS.DELETE_VEHICLES);
    
    if (!hasDeletePermission) {
      if (Platform.OS === 'web') {
        alert('ليس لديك صلاحية لحذف المركبات\nYou do not have permission to delete vehicles');
      } else {
        Alert.alert(
          'صلاحية مطلوبة / Permission Required',
          'ليس لديك صلاحية لحذف المركبات\nYou do not have permission to delete vehicles',
          [{ text: 'حسناً / OK' }]
        );
      }
      return;
    }
    
    if (Platform.OS === 'web') {
      const confirmed = window.confirm(
        `هل أنت متأكد من حذف ${vehicle.makeModel} (${vehicle.licensePlate})?\nAre you sure you want to delete ${vehicle.makeModel} (${vehicle.licensePlate})?`
      );
      if (confirmed) {
        console.log('Delete vehicle:', vehicle.id);
        removeVehicle(vehicle.id).then(() => {
          console.log('✅ Vehicle deleted successfully');
          fetchVehicles(); // Refresh the list
        }).catch((error) => {
          console.error('❌ Delete failed:', error);
          alert('Failed to delete vehicle: ' + error.message);
        });
      }
    } else {
      Alert.alert(
        'حذف المركبة / Delete Vehicle',
        `هل أنت متأكد من حذف ${vehicle.makeModel} (${vehicle.licensePlate})?\nAre you sure you want to delete ${vehicle.makeModel} (${vehicle.licensePlate})?`,
        [
          { text: 'إلغاء / Cancel', style: 'cancel' },
          { text: 'حذف / Delete', style: 'destructive', onPress: async () => {
            try {
              console.log('Delete vehicle:', vehicle.id);
              await removeVehicle(vehicle.id);
              console.log('✅ Vehicle deleted successfully');
              await fetchVehicles(); // Refresh the list
            } catch (error: any) {
              console.error('❌ Delete failed:', error);
              Alert.alert('Error', 'Failed to delete vehicle: ' + error.message);
            }
          }}
        ]
      );
    }
  };

  const handleAssignDriver = (vehicle: Vehicle) => {
    // Navigate to Assignment section with BilingualAssignmentForm
    if (Platform.OS === 'web') {
      // Web: Use custom navigation context to switch sections
      // Store pre-selection in session storage for AssignmentScreen to pick up
      sessionStorage.setItem('assignmentPreSelection', JSON.stringify({
        preSelectedVehicleId: vehicle.id,
        preSelectedFormType: 'delivery'
      }));
      setCurrentRoute('Assignments');
    } else {
      // Mobile: Use React Navigation
      navigation.navigate('Assignments', {
        screen: 'AssignmentMain',
        params: {
          preSelectedVehicleId: vehicle.id,
          preSelectedFormType: 'delivery'
        }
      });
    }
  };

  const handleUnassignDriver = (vehicle: Vehicle) => {
    // Navigate to Assignment section with BilingualAssignmentForm for receiving
    if (Platform.OS === 'web') {
      // Web: Use custom navigation context
      sessionStorage.setItem('assignmentPreSelection', JSON.stringify({
        preSelectedVehicleId: vehicle.id,
        preSelectedFormType: 'receiving'
      }));
      setCurrentRoute('Assignments');
    } else {
      // Mobile: Use React Navigation
      navigation.navigate('Assignments', {
        screen: 'AssignmentMain',
        params: {
          preSelectedVehicleId: vehicle.id,
          preSelectedFormType: 'receiving'
        }
      });
    }
  };

  const handleTrackVehicle = (vehicle: Vehicle) => {
    if (isComprehensiveVehicleTracking) {
      navigation.navigate('VehicleTracking', { vehicleId: vehicle.id });
    } else {
      Alert.alert('Coming Soon', 'Vehicle tracking feature will be available in the next update!');
    }
  };

  const handleMaintenanceSchedule = (vehicle: Vehicle) => {
    if (isComprehensiveVehicleTracking) {
      navigation.navigate('MaintenanceSchedule', { vehicleId: vehicle.id });
    } else {
      Alert.alert('Coming Soon', 'Maintenance scheduling will be available soon!');
    }
  };

  const handleBulkAction = (action: 'export' | 'maintenance' | 'status') => {
    if (action === 'export') {
      navigation.navigate('VehicleExport');
    } else {
      Alert.alert(
        'Bulk Action',
        `${action} feature will be implemented in the next update!`,
        [{ text: 'OK' }]
      );
    }
  };

  // Enhanced fleet analytics header
  const renderFleetAnalytics = () => {
    const availableCount = vehicles.filter(v => v.carStatus === 'available').length;
    const workCount = vehicles.filter(v => v.carStatus === 'work').length;
    const maintenanceCount = vehicles.filter(v => v.carStatus === 'in_workshop').length;
    const totalVehicles = vehicles.length;
    const utilizationRate = totalVehicles > 0 ? Math.round((workCount / totalVehicles) * 100) : 0;

    return (
      <ComprehensiveVehicleTrackingGuard>
        <Card style={styles.analyticsCard}>
          <View style={styles.analyticsHeader}>
            <Text style={styles.analyticsTitle}>Fleet Analytics</Text>
            <Text style={styles.analyticsSubtitle}>Total: {totalVehicles} vehicles</Text>
          </View>
          <View style={styles.analyticsRow}>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{availableCount}</Text>
              <Text style={styles.analyticsLabel}>Available</Text>
            </View>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{workCount}</Text>
              <Text style={styles.analyticsLabel}>In Service</Text>
            </View>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{utilizationRate}%</Text>
              <Text style={styles.analyticsLabel}>Utilization</Text>
            </View>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{maintenanceCount}</Text>
              <Text style={styles.analyticsLabel}>Maintenance</Text>
            </View>
          </View>
        </Card>
      </ComprehensiveVehicleTrackingGuard>
    );
  };

  // Enhanced controls with advanced sorting and view options
  const renderEnhancedControls = () => (
    <ComprehensiveVehicleTrackingGuard>
      <View style={styles.controlsContainer}>
        <View style={styles.sortingContainer}>
          <Text style={styles.controlLabel}>Sort by:</Text>
          <View style={styles.sortButtons}>
            {[
              { key: 'plate', label: 'Plate' },
              { key: 'utilization', label: 'Usage' },
              { key: 'maintenance', label: 'Maint.' },
              { key: 'location', label: 'Location' },
            ].map((option) => (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.sortButton,
                  sortBy === option.key && styles.sortButtonActive,
                ]}
                onPress={() => setSortBy(option.key as any)}
              >
                <Text style={[
                  styles.sortButtonText,
                  sortBy === option.key && styles.sortButtonTextActive,
                ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        
        <View style={styles.viewModeContainer}>
          <TouchableOpacity
            style={[styles.viewModeButton, viewMode === 'grid' && styles.viewModeButtonActive]}
            onPress={() => setViewMode('grid')}
          >
            <Ionicons name="grid-outline" size={18} color={
              viewMode === 'grid' ? theme.colors.white : theme.colors.gray600
            } />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.viewModeButton, viewMode === 'list' && styles.viewModeButtonActive]}
            onPress={() => setViewMode('list')}
          >
            <Ionicons name="list-outline" size={18} color={
              viewMode === 'list' ? theme.colors.white : theme.colors.gray600
            } />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.viewModeButton, viewMode === 'map' && styles.viewModeButtonActive]}
            onPress={() => setViewMode('map')}
          >
            <Ionicons name="location-outline" size={18} color={
              viewMode === 'map' ? theme.colors.white : theme.colors.gray600
            } />
          </TouchableOpacity>
        </View>
      </View>
    </ComprehensiveVehicleTrackingGuard>
  );

  // Enhanced action buttons with tracking features
  const renderActionButtons = () => (
    <View style={styles.actionButtonsContainer}>
      <Button
        title="Add Vehicle"
        variant="primary"
        size="small"
        leftIcon={<Ionicons name="car-outline" size={16} color="white" />}
        onPress={handleAddVehicle}
        style={styles.actionButton}
      />
      
      <ComprehensiveVehicleTrackingGuard>
        <Button
          title="Fleet Monitor"
          variant="secondary"
          size="small"
          leftIcon={<Ionicons name="radio-outline" size={16} color="white" />}
          onPress={() => navigation.navigate('FleetMonitor')}
          style={styles.actionButton}
        />
        <Button
          title="Maintenance"
          variant="secondary"
          size="small"
          leftIcon={<Ionicons name="construct-outline" size={16} color="white" />}
          onPress={() => handleBulkAction('maintenance')}
          style={styles.actionButton}
        />
        <Button
          title="Export"
          variant="outline"
          size="small"
          leftIcon={<Ionicons name="download-outline" size={16} color={theme.colors.primary} />}
          onPress={() => handleBulkAction('export')}
          style={styles.actionButton}
        />
      </ComprehensiveVehicleTrackingGuard>
    </View>
  );

  const handleAddVehicle = () => {
    navigation.navigate('AddVehicle');
  };

  // Enhanced filtering and sorting with advanced tracking
  const filteredVehicles = vehicles
    .filter((vehicle) => {
      const matchesSearch =
        vehicle.licensePlate.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.makeModel.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter ? vehicle.carStatus === statusFilter : true;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'utilization' && isComprehensiveVehicleTracking) {
        // Mock utilization score - in real app, calculate from usage data
        const aUtilization = Math.random() * 100;
        const bUtilization = Math.random() * 100;
        return bUtilization - aUtilization;
      }
      
      if (sortBy === 'maintenance' && isComprehensiveVehicleTracking) {
        // Sort by maintenance priority (mock data)
        const aMaintenanceScore = Math.random() * 100;
        const bMaintenanceScore = Math.random() * 100;
        return bMaintenanceScore - aMaintenanceScore;
      }
      
      if (sortBy === 'location' && isComprehensiveVehicleTracking) {
        // Sort by location (mock data)
        return a.licensePlate.localeCompare(b.licensePlate);
      }
      
      // Default sort logic
      const aIsAvailable = a.carStatus === 'available';
      const bIsAvailable = b.carStatus === 'available';
      
      if (aIsAvailable && !bIsAvailable) return -1;
      if (!aIsAvailable && bIsAvailable) return 1;
      
      return a.licensePlate.localeCompare(b.licensePlate);
    });

  const renderFilterButton = (status: string | null, label: string) => (
    <TouchableOpacity
      style={[
        styles.filterButton,
        statusFilter === status && styles.filterButtonActive,
      ]}
      onPress={() => setStatusFilter(status)}
    >
      <Text
        style={[
          styles.filterButtonText,
          statusFilter === status && styles.filterButtonTextActive,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      {/* Vehicle List/Grid with Header Content */}
      {loading && vehicles.length === 0 ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <FlatList
          data={filteredVehicles}
          keyExtractor={(item) => item.id}
          numColumns={viewMode === 'grid' ? numColumns : 1}
          key={`${numColumns}-${viewMode}`} // Force re-render when layout changes
          ListHeaderComponent={
            <>
              {/* Enhanced Fleet Analytics */}
              {renderFleetAnalytics()}

              {/* Enhanced Search Container */}
              <View style={styles.searchContainer}>
                <Ionicons name="search" size={20} color={theme.colors.gray400} style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search by plate or model..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  clearButtonMode="while-editing"
                />
              </View>

              {/* Enhanced Controls */}
              {renderEnhancedControls()}

              {/* Status Filters */}
              <View style={styles.filtersContainer}>
                <Text style={styles.filtersTitle}>Filter by Status:</Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.filtersScrollContent}
                >
                  {renderFilterButton(null, 'All')}
                  {renderFilterButton('available', 'Available')}
                  {renderFilterButton('work', 'In Service')}
                  {renderFilterButton('in_workshop', 'In Workshop')}
                  {renderFilterButton('out_of_service', 'Out of Service')}
                </ScrollView>
              </View>

              {/* Action Buttons */}
              {renderActionButtons()}

              {/* Vehicle Count */}
              <View style={styles.countContainer}>
                <Text style={styles.countText}>
                  {filteredVehicles.length} of {vehicles.length} vehicles
                </Text>
                
                <ComprehensiveVehicleTrackingGuard>
                  <Text style={styles.performanceText}>
                    Sorted by {sortBy} • {viewMode} view
                  </Text>
                </ComprehensiveVehicleTrackingGuard>
              </View>
            </>
          }
          renderItem={({ item }) => (
            <VehicleGridCard
              vehicle={item}
              onPress={() => handleVehiclePress(item)}
              onViewDetails={() => handleVehiclePress(item)}
              onEdit={() => handleEditVehicle(item)}
              onDelete={() => handleDeleteVehicle(item)}
              onAssign={() => handleAssignDriver(item)}
              onUnassign={() => handleUnassignDriver(item)}
            />
          )}
          contentContainerStyle={styles.gridContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[theme.colors.primary]}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="car-outline" size={64} color={theme.colors.gray300} />
              <Text style={styles.emptyText}>No vehicles found</Text>
              <Text style={styles.emptyDescription}>
                {searchQuery || statusFilter
                  ? 'Try adjusting your search or filters'
                  : 'Add your first vehicle to get started with fleet management'}
              </Text>
              <Button
                title="Add Your First Vehicle"
                onPress={handleAddVehicle}
                variant="primary"
                style={styles.emptyButton}
                leftIcon={<Ionicons name="add-circle-outline" size={20} color="white" />}
              />
            </View>
          }
        />
      )}

      {/* Error State */}
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
          <Button title="Try Again" onPress={handleRefresh} variant="outline" />
        </View>
      )}

      {/* Floating Manual Sync Button */}
      <ManualSyncButton
        variant="fab"
        onSyncComplete={(success) => {
          if (success) {
            // Refresh vehicle data after successful sync
            fetchVehicles();
          }
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  analyticsCard: {
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  analyticsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  analyticsTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  analyticsSubtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    fontWeight: '500' as any,
  },
  viewDetailsText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.primary,
    fontWeight: '500' as any,
  },
  analyticsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  analyticsStat: {
    alignItems: 'center',
    flex: 1,
  },
  analyticsValue: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.primary,
  },
  analyticsLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.xs,
  },
  controlsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  sortingContainer: {
    flex: 1,
  },
  controlLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
  },
  sortButtons: {
    flexDirection: 'row',
  },
  sortButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    marginRight: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.gray100,
  },
  sortButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  sortButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  sortButtonTextActive: {
    color: theme.colors.white,
  },
  viewModeContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.sm,
    padding: 2,
  },
  viewModeButton: {
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
  },
  viewModeButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    paddingHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  actionButton: {
    marginRight: theme.spacing.sm,
  },
  countContainer: {
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.xs,
  },
  countText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  performanceText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    ...theme.shadows.sm,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  searchContainer: {
    flexDirection: 'row',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
    alignItems: 'center',
    gap: 12,
  },
  searchIcon: {
    marginRight: theme.spacing.sm,
  },
  searchInput: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: Platform.OS === 'web' ? theme.spacing.sm : theme.spacing.md,
    fontSize: Platform.OS === 'web' ? theme.typography.sizes.md : theme.typography.sizes.lg,
  },
  filtersContainer: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  filtersTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    marginBottom: theme.spacing.sm,
    color: theme.colors.textSecondary,
  },
  filtersScrollContent: {
    paddingBottom: theme.spacing.xs,
  },
  filterButton: {
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.round,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    marginRight: theme.spacing.sm,
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  filterButtonTextActive: {
    color: theme.colors.white,
  },
  listContent: {
    padding: theme.spacing.md,
  },
  gridContent: {
    padding: theme.spacing.sm,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  errorContainer: {
    padding: theme.spacing.lg,
    alignItems: 'center',
  },
  errorText: {
    color: theme.colors.error,
    fontSize: theme.typography.sizes.md,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
    marginTop: theme.spacing.xl,
  },
  emptyText: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.lg,
  },
  emptyButton: {
    marginTop: theme.spacing.md,
  },
});
